import React, { useEffect, useState, useRef } from "react";
import JsonNode from "./JsonNode";

interface RawJsonDataDisplayProps {
  jsons: any[];
  index: number;
}

interface SearchResult {
  path: string;
  value: any;
  id: string;
}

// Fonction utilitaire pour copier du texte dans le presse-papiers
const copyToClipboard = (text: string) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      // Succès silencieux
    })
    .catch((err) => {
      console.error("Erreur lors de la copie du chemin:", err);
    });
};

const RawJsonDataDisplay: React.FC<RawJsonDataDisplayProps> = ({
  index,
  jsons,
}) => {
  const jsonData = jsons[index];
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [selectedResult, setSelectedResult] = useState<number | null>(null);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [notificationVisible, setNotificationVisible] = useState(false);
  const [copiedPath, setCopiedPath] = useState("");
  const resultRefs = useRef<{ [id: string]: HTMLElement | null }>({});
  const contentRef = useRef<HTMLDivElement>(null);

  // Fonction améliorée pour rechercher récursivement dans l'objet JSON
  const searchInJson = (
    obj: any,
    currentPath: string = "",
    results: SearchResult[] = [],
    visitedPaths = new Set<string>(),
  ): SearchResult[] => {
    if (!obj || typeof obj !== "object") return results;

    // Éviter les boucles infinies avec des objets circulaires
    if (visitedPaths.has(currentPath)) return results;
    if (currentPath) visitedPaths.add(currentPath);

    if (Array.isArray(obj)) {
      obj.forEach((item, idx) => {
        const newPath = `${currentPath}[${idx}]`;

        // Vérifier si la valeur correspond au terme de recherche
        if (
          typeof item !== "object" &&
          String(item).toLowerCase().includes(searchTerm.toLowerCase())
        ) {
          // Vérifier si ce chemin n'a pas déjà été ajouté
          const resultExists = results.some((r) => r.path === newPath);
          if (!resultExists) {
            results.push({
              path: newPath,
              value: item,
              id: `result-${results.length}`,
            });
          }
        }

        // Rechercher récursivement dans les objets/tableaux
        if (item && typeof item === "object") {
          searchInJson(item, newPath, results, new Set(visitedPaths));
        }
      });
    } else {
      Object.entries(obj).forEach(([key, value]) => {
        const newPath = currentPath ? `${currentPath}.${key}` : key;

        // Vérifier si la clé correspond au terme de recherche
        if (key.toLowerCase().includes(searchTerm.toLowerCase())) {
          const resultExists = results.some((r) => r.path === newPath);
          if (!resultExists) {
            results.push({
              path: newPath,
              value: typeof value === "object" ? "Object" : value,
              id: `result-${results.length}`,
            });
          }
        }

        // Vérifier si la valeur correspond au terme de recherche
        if (
          typeof value !== "object" &&
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        ) {
          const resultExists = results.some((r) => r.path === newPath);
          if (!resultExists) {
            results.push({
              path: newPath,
              value,
              id: `result-${results.length}`,
            });
          }
        }

        // Rechercher récursivement dans les objets/tableaux
        if (value && typeof value === "object") {
          searchInJson(value, newPath, results, new Set(visitedPaths));
        }
      });
    }
    return results;
  };

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setSearchResults([]);
      setSelectedResult(null);
      return;
    }

    const results = searchInJson(jsonData);

    // Vérification supplémentaire pour éliminer les doublons potentiels
    const uniqueResults = results.filter(
      (result, index, self) =>
        index === self.findIndex((r) => r.path === result.path),
    );

    setSearchResults(uniqueResults);
    setSelectedResult(uniqueResults.length > 0 ? 0 : null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, jsonData]);

  // Fonction pour enregistrer les références des éléments
  const setResultRef = (id: string, element: HTMLElement | null) => {
    if (!element) return;

    const newPriority = parseInt(
      element.getAttribute("data-priority") || "0",
      10,
    );
    const currentElement = resultRefs.current[id];

    // Si aucun élément n'est encore enregistré pour cet ID, enregistrer celui-ci
    if (!currentElement) {
      resultRefs.current[id] = element;
      return;
    }

    // Si un élément est déjà enregistré, comparer les priorités
    const currentPriority = parseInt(
      currentElement.getAttribute("data-priority") || "0",
      10,
    );

    // Si le nouvel élément a une priorité plus élevée, le remplacer
    if (newPriority > currentPriority) {
      resultRefs.current[id] = element;
    }

    // Si les priorités sont égales, vérifier si l'un des éléments correspond exactement au chemin recherché
    else if (newPriority === currentPriority) {
      const isExactMatch = element.getAttribute("data-exact-match") === "true";
      const isCurrentExactMatch =
        currentElement.getAttribute("data-exact-match") === "true";

      if (isExactMatch && !isCurrentExactMatch) {
        resultRefs.current[id] = element;
      }
    }
  };

  // Effet pour faire défiler vers le résultat sélectionné
  useEffect(() => {
    if (selectedResult !== null && searchResults.length > 0) {
      const currentResult = searchResults[selectedResult];

      // Attendre que toutes les références soient enregistrées
      setTimeout(() => {
        const element = resultRefs.current[currentResult.id];

        if (element) {
          // Utiliser un petit délai pour s'assurer que tous les éléments sont rendus
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });

          // Ajouter une classe temporaire pour mettre en évidence l'élément
          element.classList.add("flash-highlight");
          setTimeout(() => {
            element.classList.remove("flash-highlight");
          }, 1000);
        }
      }, 100);
    }
  }, [selectedResult, searchResults]);

  const goToPrevResult = () => {
    if (searchResults.length === 0) return;
    setSelectedResult((prev) =>
      prev === null || prev <= 0 ? searchResults.length - 1 : prev - 1,
    );
  };

  const goToNextResult = () => {
    if (searchResults.length === 0) return;
    setSelectedResult((prev) =>
      prev === null || prev >= searchResults.length - 1 ? 0 : prev + 1,
    );
  };

  // Fonction pour gérer le clic sur un chemin
  const handlePathClick = (path: string) => {
    copyToClipboard(path);
    setSelectedPath(path);
    setCopiedPath(path);

    // Afficher la notification
    setNotificationVisible(true);

    // Masquer la notification après un délai
    setTimeout(() => {
      setNotificationVisible(false);
    }, 2000);

    // Réinitialiser l'état de surbrillance après un court délai
    setTimeout(() => {
      setSelectedPath(null);
    }, 1500);
  };

  return (
    <div style={{ position: "relative", height: "100%" }}>
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          backgroundColor: "white",
          padding: "10px 0",
          borderBottom: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "15px",
        }}
      >
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Rechercher dans le JSON..."
          style={{
            padding: "8px",
            width: "300px",
            borderRadius: "4px",
            border: "1px solid #ccc",
          }}
        />
        {searchResults.length > 0 && (
          <>
            <button onClick={goToPrevResult} style={{ padding: "5px 10px" }}>
              ←
            </button>
            <span>
              {selectedResult !== null ? selectedResult + 1 : 0} /{" "}
              {searchResults.length}
            </span>
            <button onClick={goToNextResult} style={{ padding: "5px 10px" }}>
              →
            </button>
          </>
        )}
      </div>

      <div
        ref={contentRef}
        style={{
          fontFamily: "monospace",
          fontSize: "14px",
          overflowY: "auto",
          height: "calc(100% - 60px)",
        }}
      >
        <JsonNode
          data={jsonData}
          searchTerm={searchTerm}
          highlightPath={
            selectedResult !== null
              ? searchResults[selectedResult].path
              : undefined
          }
          resultId={
            selectedResult !== null
              ? searchResults[selectedResult].id
              : undefined
          }
          setResultRef={setResultRef}
          onPathClick={handlePathClick}
          selectedPath={selectedPath}
        />
      </div>

      {notificationVisible && (
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            backgroundColor: "#4CAF50",
            color: "white",
            padding: "10px 15px",
            borderRadius: "4px",
            boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
            zIndex: 1000,
            opacity: 1,
            transition: "opacity 0.3s ease-in-out",
          }}
        >
          <div>Chemin copié !</div>
          <div
            style={{
              fontSize: "12px",
              marginTop: "5px",
              wordBreak: "break-all",
            }}
          >
            {copiedPath}
          </div>
        </div>
      )}

      <style>
        {`
          .flash-highlight {
            animation: flash-animation 1s;
            outline: 2px solid #ff6600;
            outline-offset: 2px;
          }
          
          @keyframes flash-animation {
            0% { background-color: #ff6600; }
            100% { background-color: transparent; }
          }
        `}
      </style>
    </div>
  );
};

export default RawJsonDataDisplay;
